import {
  base64EncodeUnicode,
  useABAC,
  useSettings,
} from '@jupiterone/jupiter-web-toolkit';
import debounce from 'lodash/debounce';
import { useEffect, useState } from 'react';
import {
  GraphViewType,
  NodePositionMap,
  SelectedNode,
} from '../components/types';
import convertLegacySaveToNewSave from './utils/convertLegacySaveToNewSave';

const isLegacySave = (data: SaveData | LegacySaveData): boolean =>
  Object.keys(data).find((k) => k === 'lastUsedGraphType') === undefined;

function formatQuery(query?: string) {
  return query ? query.toLowerCase().replace('return tree', '').trim() : '';
}
export interface LegacySaveData {
  [nodeId: string]: number[];
}

export interface SaveData {
  [key: string]: NodePositionMap | GraphViewType;
  lastUsedGraphType: GraphViewType;
}

interface HookReturnValue {
  loading: boolean;
  positions: NodePositionMap;
  onUpdateNodePositions: (nodes: SelectedNode[], clear?: boolean) => void;
}

type SettingsReturnValue = [
  {
    data: LegacySaveData | SaveData | null;
    loading: boolean;
  },
  (saveData: SaveData) => void
];
const NODE_POSITION_KEY = 'graphviewer/nodepositions/';

const useSavedNodePositions = (
  query?: string,
  graphType: GraphViewType = GraphViewType.mesh
): HookReturnValue => {
  const { enforce } = useABAC();
  const isAdmin = enforce([]);
  const category = `${NODE_POSITION_KEY}${base64EncodeUnicode(
    formatQuery(query)
  )}`;
  const resourceType = 'account';
  const [saveData, setSaveData] = useState<SaveData>();
  const [{ data, loading }, setSettings] = useSettings(
    resourceType,
    category,
    undefined
  ) as SettingsReturnValue;

  const [positionMap, setPositionMap] = useState<NodePositionMap | null>(null);

  const handleNodePositionUpdate = () => {
    if (query && positionMap && Object.keys(positionMap).length) {
      debounce(() => savePositions(positionMap), 3000, {
        leading: true,
        trailing: false,
        maxWait: 1000,
      })();
    }
  };

  useEffect(() => {
    if (data) {
      const newSaveData = isLegacySave(data)
        ? convertLegacySaveToNewSave(data as LegacySaveData)
        : (data as SaveData);
      setSaveData(newSaveData);
    }
  }, [data]);

  useEffect(() => {
    if (saveData) {
      setPositionMap((saveData?.[graphType] as NodePositionMap) || {});
    }
  }, [saveData]);

  useEffect(() => {
    if (saveData) {
      setPositionMap((saveData[graphType] as NodePositionMap) || {});
    }
  }, [graphType]);

  useEffect(handleNodePositionUpdate, [positionMap]);

  if (!query) {
    return { loading: false, positions: {}, onUpdateNodePositions: () => ({}) };
  }

  const savePositions = (positionMap: NodePositionMap) => {
    if (isAdmin) {
      setSettings({
        ...saveData,
        lastUsedGraphType: graphType,
        [graphType]: positionMap,
      });
    }
  };

  const onUpdateNodePositions = (nodes: SelectedNode[], clear?: boolean) => {
    const newMap = clear
      ? { empty: [0, 0] }
      : nodes.reduce(
          (acc: NodePositionMap, node) => {
            if (node.id) {
              acc[node.id] = node.position;
            }
            return acc;
          },
          { ...positionMap }
        );
    setPositionMap(newMap);
  };

  return {
    loading: loading,
    positions: saveData && positionMap ? positionMap : {},
    onUpdateNodePositions,
  };
};

export default useSavedNodePositions;
